import { MessageResponseTypes, type ChatInstance } from '@carbon/ai-chat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TitleCalculationActivity } from 'atoms/title-calculation-atoms';
import { AgentSurface } from 'components/agent-chat';
import { TITLE_WORKSPACE_AGENT } from 'components/agent-chat/agents/title-workspace-agent';

const formatLastCalculated = (
  iso: string | null | undefined
): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date} ${time}`;
};

const formatTimeOfDay = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// Pushes recalculation notices into the transcript.
//
// Notices are ephemeral UI hints: they are written straight to the live chat
// and never persisted server-side, so re-hydrating the thread will not bring
// them back.
const useCalculationNotices = (
  instance: ChatInstance | null,
  activity: TitleCalculationActivity | undefined
) => {
  const seenStartedAt = useRef<string | null>(activity?.startedAt ?? null);
  const seenCompletedAt = useRef<string | null>(activity?.completedAt ?? null);

  const addNotice = useCallback(
    (id: string, title: string) => {
      void instance?.messaging.addMessage({
        id,
        output: {
          // Carbon renders SYSTEM items as centered status lines, which is what
          // these are — not something the assistant said.
          generic: [{ response_type: MessageResponseTypes.SYSTEM, title }],
        },
      });
    },
    [instance]
  );

  useEffect(() => {
    const startedAt = activity?.startedAt ?? null;
    if (!instance || !startedAt || seenStartedAt.current === startedAt) return;
    seenStartedAt.current = startedAt;
    const time = formatTimeOfDay(startedAt);
    addNotice(
      `notice-start-${startedAt}`,
      `Workspace recalculation triggered${time ? ` at ${time}` : ''} — ` +
        'new questions will use the updated calculation once it completes.'
    );
  }, [activity?.startedAt, addNotice, instance]);

  useEffect(() => {
    const completedAt = activity?.completedAt ?? null;
    if (!instance || !completedAt || seenCompletedAt.current === completedAt) {
      return;
    }
    seenCompletedAt.current = completedAt;
    const time = formatTimeOfDay(completedAt);
    addNotice(
      `notice-complete-${completedAt}`,
      `Workspace recalculation completed${time ? ` at ${time}` : ''} — ` +
        'earlier answers reflect the prior calculation.'
    );
  }, [activity?.completedAt, addNotice, instance]);
};

export const TitleWorkspaceAgent = ({
  agentMessagesHref,
  workspaceId,
  workspaceName,
  lastCalculated,
  model,
  calculationActivity,
}: {
  agentMessagesHref: string;
  workspaceId: number | string;
  workspaceName: string;
  lastCalculated?: string | null;
  model?: string | null;
  calculationActivity?: TitleCalculationActivity;
}) => {
  const [instance, setInstance] = useState<ChatInstance | null>(null);
  useCalculationNotices(instance, calculationActivity);

  const subtitle = useMemo(() => {
    const parts: string[] = [`Ask about ${workspaceName}`];
    const calc = formatLastCalculated(lastCalculated);
    if (calc) parts.push(`Last calculated ${calc}`);
    return parts.join(' · ');
  }, [workspaceName, lastCalculated]);

  return (
    <AgentSurface
      agent={TITLE_WORKSPACE_AGENT}
      agentMessagesHref={agentMessagesHref}
      subject={{ type: 'TitleWorkspace', id: workspaceId }}
      subtitle={subtitle}
      model={model}
      disabledReason={
        calculationActivity?.isCalculating
          ? 'Workspace is calculating...'
          : null
      }
      onInstanceReady={setInstance}
    />
  );
};
